import Query from '../../query-builder';
import ReduxHooks from '../../../store/ReduxHooks';
import { actionCreators } from '../../../store/actions';
import ApiResourceManager from '../ApiResourceManager';
import { buildCompaniesQuery, buildDeliverySchedulePayload } from '../transforms';

export default class CompaniesManager extends ApiResourceManager {
  create = (data) =>
    this.request({
      method: 'POST',
      url: '/companies',
      data,
    });

  /**
   * The IANA timezone for an address. Country and state usually settle it on
   * their own — every European region keeps one clock — and the coordinates are
   * only consulted where a zone boundary actually runs through the region.
   * Resolves to null when nothing can answer, and the caller must then let the
   * operator pick rather than fall back to a guess.
   */
  inferTimezone = async ({ country, state, lat, lon }) => {
    const response = await this.request({
      method: 'POST',
      url: '/companies/infer-timezone',
      data: { country, state, lat, lon },
    });

    return response.success ? (response.data?.data?.timezone ?? null) : null;
  };

  export = () =>
    this.request({
      method: 'GET',
      url: '/all-companies/export',
      responseType: 'blob',
    });

  getAll = (limit = null) =>
    this.request({
      method: 'GET',
      url: new Query().for('all-companies').limit(limit).get(),
    });

  getWithUnfulfilledSetupPackage = (limit = null) =>
    this.request({
      method: 'GET',
      url: new Query().for('all-companies').where('with_unfulfilled_setup_package', true).limit(limit).get(),
    });

  get = (data = {}, sort = '-companies.id') =>
    this.request({
      method: 'GET',
      url: buildCompaniesQuery(data, sort),
    });

  show = async (id) => {
    const response = await this.request({
      method: 'GET',
      url: `/companies/${id}`,
    });

    if (response.success) {
      ReduxHooks.dispatch(actionCreators.currentCompany.set(response.data));
    }

    return response;
  };

  /**
   * The same read as `show`, without making the answer the selected
   * restaurant: for a caller that must first check the selection has not
   * moved on while the request was out.
   */
  fetch = (id) =>
    this.request({
      method: 'GET',
      url: `/companies/${id}`,
    });

  update = async (id, data) =>
    this.request({
      method: 'PUT',
      url: `/companies/${id}`,
      data,
    });

  partialUpdate = async (id, data) =>
    this.request({
      method: 'PATCH',
      url: `/companies/${id}`,
      data,
    });

  delete = async (id) =>
    this.request({
      method: 'DELETE',
      url: `/companies/${id}`,
    });

  updateAndSetCurrentCompany = async (id, data) => {
    const response = await this.update(id, data);

    if (response.success) {
      ReduxHooks.dispatch(actionCreators.currentCompany.set(response.data));
    }

    return response;
  };

  updateSettings = async (data) => {
    const response = await this.request({
      method: 'PUT',
      url: '/settings',
      data,
    });

    if (response.success) {
      ReduxHooks.dispatch(actionCreators.currentCompany.set(response.data));
    }

    return response;
  };

  updateMenuSettings = async (data) =>
    this.request({
      method: 'PUT',
      url: '/menu-settings',
      data,
    });

  updateTakeoutSettings = async (data) => {
    const response = await this.request({
      method: 'PUT',
      url: '/takeout-settings',
      data,
    });

    if (response.success) {
      ReduxHooks.dispatch(actionCreators.currentCompany.set(response.data));
    }

    return response;
  };

  updateOperationalStatus = async (data) => {
    const response = await this.request({
      method: 'PUT',
      url: '/operational-status',
      data,
    });

    if (response.success) {
      ReduxHooks.dispatch(actionCreators.currentCompany.set(response.data));
    }

    return response;
  };

  updateTipSettings = async (data) => {
    const response = await this.request({
      method: 'PUT',
      url: '/tip-settings',
      data,
    });

    if (response.success) {
      ReduxHooks.dispatch(actionCreators.currentCompany.set(response.data));
    }

    return response;
  };

  updateOrderLayoutSettings = async (data) => {
    const response = await this.request({
      method: 'PUT',
      url: '/order-layout-settings',
      data,
    });

    if (response.success) {
      ReduxHooks.dispatch(actionCreators.currentCompany.set(response.data));
    }

    return response;
  };

  updateReceiptSettings = async (data) => {
    const response = await this.request({
      method: 'PUT',
      url: '/receipt-settings',
      data,
    });

    if (response.success) {
      ReduxHooks.dispatch(actionCreators.currentCompany.set(response.data));
    }

    return response;
  };

  updateWorkSchedule = async (data) => {
    const response = await this.request({
      method: 'PUT',
      url: '/work-schedule',
      data: {
        opening_hours: data,
      },
    });

    if (response.success) {
      ReduxHooks.dispatch(actionCreators.currentCompany.set(response.data));
    }

    return response;
  };

  updateDeliverySchedule = async (data) => {
    const response = await this.request({
      method: 'PUT',
      url: '/delivery-schedule',
      data: buildDeliverySchedulePayload(data),
    });

    if (response.success) {
      ReduxHooks.dispatch(actionCreators.currentCompany.set(response.data));
    }

    return response;
  };

  updateHolidays = async (data) => {
    const response = await this.request({
      method: 'PUT',
      url: '/holidays',
      data,
    });

    if (response.success) {
      ReduxHooks.dispatch(actionCreators.currentCompany.set(response.data));
    }

    return response;
  };

  toggleDeliverySchedule = async (hasDeliverySchedule) => {
    const response = await this.request({
      method: 'PUT',
      url: '/delivery-schedule',
      data: {
        has_delivery_schedule: hasDeliverySchedule,
      },
    });

    if (response.success) {
      ReduxHooks.dispatch(actionCreators.currentCompany.set(response.data));
    }

    return response;
  };

  updateLocation = async (data) => {
    const response = await this.request({
      method: 'PUT',
      url: '/location',
      data,
    });

    if (response.success) {
      ReduxHooks.dispatch(actionCreators.currentCompany.set(response.data));
    }

    return response;
  };

  updateGoogleProfile = async (data) => {
    const response = await this.request({
      method: 'PUT',
      url: '/google-profile',
      data,
    });

    if (response.success) {
      ReduxHooks.dispatch(actionCreators.currentCompany.set(response.data));
    }

    return response;
  };

  getIntegrationsSettings = async () =>
    this.request({
      method: 'GET',
      url: '/integrations',
    });

  createWinOrderIntegration = async (data) =>
    this.request({
      method: 'POST',
      url: '/integrations/win_order',
      data,
    });

  saveIntegration = async (data) =>
    this.request({
      method: 'PUT',
      url: '/integrations',
      data,
    });

  deleteIntegration = async (service) =>
    this.request({
      method: 'DELETE',
      url: '/integrations',
      data: { service },
    });

  // Erase a customer's mirrored data on the connected POS systems. `service`
  // scopes the erasure to one integration; null erases it everywhere.
  deleteIntegrationCustomer = async (customerId, service = null) =>
    this.request({
      method: 'DELETE',
      url: `/integrations/customers/${customerId}`,
      data: { service },
    });

  connectHubrise = async (data) =>
    this.request({
      method: 'POST',
      url: '/integrations/hubrise/connect',
      data,
    });

  updateIntegrationDetails = async (service, data) =>
    this.request({
      method: 'PATCH',
      url: `/integrations/${service}/details`,
      data,
    });

  pullIntegrationMenus = async (service) =>
    this.request({
      method: 'POST',
      url: '/integrations/menus/pull',
      data: { service },
    });

  pullIntegrationInventory = async (service) =>
    this.request({
      method: 'POST',
      url: '/integrations/inventory/pull',
      data: { service },
    });

  syncCompany = (id) =>
    this.request({
      method: 'POST',
      url: `/reservations/companies/${id}/sync`,
    });

  syncReservationScheduleExceptions = (id) =>
    this.request({
      method: 'POST',
      url: `/reservations/companies/${id}/sync-schedule-exceptions`,
    });
}
